import { Box, Grid, InputAdornment, Typography } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

/** TRANSLATION */
import { useTranslation } from 'react-i18next';

/** API */
import AdjustmentApi, { APPROVE_STATUS } from 'api/warehouses/adjustment.api';

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard';
import { notiError } from 'component/notifications/notifications';
import TableCustom from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';

/** UTILS || SLICE || CONSTANTS */
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { colors } from 'constants/theme';
import { routeName } from 'routes/routes-name';
import { toBuddhistYear } from 'utils/app.utils';
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils';

/** STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { selectMe } from 'app/slice/user.slice';
import _ from 'lodash';
import MainButton from 'new-components/buttons/main-button';
import DateRangeInput from 'new-components/inputs/date-range-input';
import SelectInput from 'new-components/inputs/select-input';
import TextInput from 'new-components/inputs/text-input';
import { useSelector } from 'react-redux';

const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
    isView: isView()
}

export default function Adjustment() {
    const { t } = useTranslation()
    const history = useHistory()
    const user = useSelector(selectMe)
    /** PAGINATION  */
    const [page, setPage] = useState(1)
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('updateAt')
    const [sortType, setSortType] = useState('DESC')
    const [search, setSearch] = useState('')
    const [status, setStatus] = useState('ALL')
    const [rangeDate, setRangeDate] = useState(['', '']);
    const [adjustments, setAdjustments] = useState([]);
    let documentStatusText = '';

    const loadData = async () => {
        try {
            const params: any = {
                page,
                pageLimit,
                sortBy,
                sortType,
                status: status === 'ALL' ? '' : status,
            };
            if (search) params.search = search;
            if (rangeDate[0] || rangeDate[1]) {
                params.dateStart = rangeDate[0];
                params.dateEnd = rangeDate[1];
            }
            const resp = await AdjustmentApi.findAll(params);
            if (resp.status === 200) {
                setRowCount(Number(resp.headers['x-total'] || 0));
                setAdjustments(resp.data);
            } else {
                setAdjustments([]);
            }
        } catch (err) {
            notiError(t('ERROR'));
            setAdjustments([]);
        }
    };
    useEffect(() => {
        loadData();
    }, [page, pageLimit, sortBy, sortType]);


    const handleChangeStatus = (statusVal: string) => {
        setStatus(statusVal)
    }

    const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
    }

    const handleChangeRowsPerPage = (limit: number) => {
        setPageLimit(limit)
    }

    const handleChangePage = (val: number) => {
        setPage(val)
    }

    const onEdit = (id: number) => {
        history.push(routeName.adjustment + '/edit', { adjustmentId: id })
    }

    const backgroundStatus = (status: APPROVE_STATUS) => {
        if (status === APPROVE_STATUS.APPROVE) {
            documentStatusText = t('INCOME_EXPENSES.DOCUMENT_STATUS.APPROVAL')
            return colors.approveStatus.approve
        }
        else if (status === APPROVE_STATUS.PENDING) {
            documentStatusText = t('INCOME_EXPENSES.DOCUMENT_STATUS.PENDING_APPROVAL')
            return colors.approveStatus.pending
        }
        else if (status === APPROVE_STATUS.CANCEL) {
            documentStatusText = t('INCOME_EXPENSES.DOCUMENT_STATUS.CANCEL')
            return colors.approveStatus.cancel
        }
        else return colors.disabledLightGray
    }

    const headCells = [
        { id: 'no', disablePadding: false, label: t('TABLE.NO'), width: '50px' },
        { id: 'message', disablePadding: false, label: t('ADJUSTMENT.TABLE.CELLS.DATE'), width: '150px' },
        { id: 'message', disablePadding: false, label: t('ADJUSTMENT.TABLE.CELLS.NAME') },
        { id: 'message', disablePadding: false, label: t('ADJUSTMENT.TABLE.CELLS.STATUS'), width: '150px' },
        { id: 'message', disablePadding: false, label: t('ADJUSTMENT.TABLE.CELLS.APPROVER'), width: '200px' },
        { id: 'updatedBy', disablePadding: false, label: t('ADJUSTMENT.TABLE.CELLS.UPDATED_BY'), width: '180px' },
        { id: 'action', disablePadding: false, label: 'Action', width: '100px' }
    ]

    const renderData = (objData: any, no: number) => {
        const { adjustmentReceiptId, date, subject, status: adjustmentStatus, updatedBy, updatedAt, createdBy, createdAt } = objData

        no = page * pageLimit - pageLimit + no + 1
        const objRenderData = {
            key: no.toString(),
            id: no.toString(),
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: no },
                { option: 'TEXT', align: 'left', label: date && date !== '0000-00-00' ? toBuddhistYear(moment(date), 'DD/MM/YYYY') : '-' },
                { option: 'TEXT', align: 'left', label: subject || '-', class: 'text-ellipsis' },
                {
                    option: 'COMPONENT',
                    align: 'left',
                    class: 'py-1',
                    component: (
                        <Box display={'flex'} alignItems={'center'} gap={'12px'}>
                            <Box sx={{ borderRadius: '50%', backgroundColor: backgroundStatus(adjustmentStatus), width: '15px', height: '15px' }} />
                            <Typography> {t(`SALARY.DOCUMENT_STATUS.${adjustmentStatus}`)}</Typography>
                        </Box>
                    )
                },
                { option: 'TEXT', align: 'left', label: updatedBy || createdBy, class: 'text-ellipsis' },
                { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy || createdBy, updatedAt: updatedAt || createdAt } },
                {
                    option: 'ACTION',
                    align: 'center',
                    label: 'action',
                    newIconAction: true,
                    values: [
                        adjustmentStatus === APPROVE_STATUS.PENDING ?
                            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled } :
                            { option: 'VIEW', label: t(`BUTTON.VIEW`), disabled: permissions.isView.disabled },
                    ]
                }
            ]
        }

        return <TableRowCommon  {...objRenderData} onedit={() => onEdit(adjustmentReceiptId)} onView={() => onEdit(adjustmentReceiptId)} />
    }

    return (
        <Box>
            <HeaderCard text={t('ADJUSTMENT.HEADER')} />
            <Box paddingX="24px" paddingTop="16px">
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            label={t('ADJUSTMENT.SEARCH')}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value)
                                setPage(1)
                            }}
                            fullWidth
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('ADJUSTMENT.FILTER.DOCUMENT_STATUS.LABEL')}
                            onChange={e => {
                                const value = e.target.value;
                                handleChangeStatus(value);
                                setPage(1);
                            }}
                            options={[
                                { value: 'ALL', name: t('INVENTORY.FILTER.GROUP.ALL') },
                                { value: APPROVE_STATUS.APPROVE, name: t(`ADJUSTMENT.FILTER.DOCUMENT_STATUS.${APPROVE_STATUS.APPROVE}`) },
                                { value: APPROVE_STATUS.PENDING, name: t(`ADJUSTMENT.FILTER.DOCUMENT_STATUS.${APPROVE_STATUS.PENDING}`) },
                                { value: APPROVE_STATUS.CANCEL, name: t(`ADJUSTMENT.FILTER.DOCUMENT_STATUS.${APPROVE_STATUS.CANCEL}`) },
                            ]}
                            value={status}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <DateRangeInput
                            allowClear
                            value={rangeDate || ['', '']}
                            onchange={(val: any) => {
                                if (_.isEmpty(val)) setRangeDate(['', ''])
                                else setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                                setPage(1)
                            }}
                            onClear={() => {
                                setPage(1)
                                setRangeDate(['', ''])
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={6}>
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                type='primary'
                                variant='contained'
                                style={{ width: '120px' }}
                                onClick={loadData} />
                            <MainButton
                                title={t('ADJUSTMENT.BUTTON.CHANGE')}
                                type='primary'
                                variant='contained'
                                onClick={() => history.push(routeName.adjustment + '/create')} startIcon={<AddCircleIcon />} />
                        </Box>
                    </Grid>
                </Grid>
                <Box paddingTop="24px">
                    <TableCustom
                        page={page}
                        pageLimit={pageLimit}
                        sortType={sortType}
                        sortBy={sortBy}
                        onSort={onRequestSort}
                        setPageLimit={handleChangeRowsPerPage}
                        setPage={handleChangePage}
                        rowCount={rowCount}
                        headCells={headCells}
                        rowsData={adjustments.map((val, i) => {
                            return renderData(val, i)
                        })}
                    />
                </Box>
            </Box>
        </Box>
    )
}
